import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Package2, ArrowUpDown } from "lucide-react";
import {
  formatCurrency,
  formatDate,
  PaginationInfo,
  ProjectStatus,
} from "@employee-management/common";
import type { Project } from "@/types/project.types";

interface ProjectTableProps {
  data?: Project[];
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
  onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
  currentSort?: { sortBy: string; sortOrder: "asc" | "desc" };
}

export function ProjectTable({
  data: propData,
  pagination,
  onPageChange,
  onSortChange,
  currentSort,
}: ProjectTableProps) {
  const navigate = useNavigate();

  // Use prop data if provided, otherwise use empty array
  const data = propData ?? [];

  const columns: ColumnDef<Project>[] = [
    {
      accessorKey: "name",
      header: () => {
        return (
          <div className="w-[25ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "name" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("name", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Project Name
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const project = row.original;
        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 bg-muted rounded-md">
              <Package2 className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">{project.name}</p>
              <p className="text-xs text-muted-foreground">
                {project.clientName}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "internalProjectId",
      header: () => {
        return (
          <div className="w-[10ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "internalProjectId" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("internalProjectId", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Internal ID
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const internalId = row.original.internalProjectId;
        return internalId || <span className="text-muted-foreground">-</span>;
      },
    },
    {
      accessorKey: "handlerMemberRef.name",
      header: () => {
        return (
          <div className="w-[16ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "handlerMemberRef.name" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("handlerMemberRef.name", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Project Supervisor
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        return <div>{row.original.handlerMemberRef?.name}</div>;
      },
    },
    {
      accessorKey: "salesTeamMemberRef.name",
      header: () => {
        return (
          <div className="w-[16ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "salesTeamMemberRef.name" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("salesTeamMemberRef.name", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Project Manager
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        return <div>{row.original.salesTeamMemberRef?.name || "—"}</div>;
      },
    },
    {
      accessorKey: "startDate",
      header: () => {
        return (
          <div className="w-[11ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "startDate" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("startDate", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Start Date
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        return <div>{formatDate(row.original.startDate)}</div>;
      },
    },

    {
      accessorKey: "status",
      header: () => {
        return (
          <div className="w-[8ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "status" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("status", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Status
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const status = row.original.status;
        if (status === ProjectStatus.ACTIVE) {
          return (
            <Badge
              variant="default"
              className="bg-green-100 text-green-800 border-green-200"
            >
              Active
            </Badge>
          );
        } else if (status === ProjectStatus.COMPLETED) {
          return (
            <Badge
              variant="secondary"
              className="bg-blue-100 text-blue-800 border-blue-200"
            >
              Completed
            </Badge>
          );
        } else if (status === ProjectStatus.ON_HOLD) {
          return (
            <Badge
              variant="secondary"
              className="bg-yellow-100 text-yellow-800 border-yellow-200"
            >
              On Hold
            </Badge>
          );
        } else if (status === ProjectStatus.CANCELLED) {
          return (
            <Badge
              variant="secondary"
              className="bg-red-100 text-red-800 border-red-200"
            >
              Cancelled
            </Badge>
          );
        } else {
          return (
            <Badge
              variant="secondary"
              className="bg-gray-100 text-gray-800 border-gray-200"
            >
              Unknown
            </Badge>
          );
        }
      },
    },
    {
      accessorKey: "metadata.totalApprovedHours",
      header: () => {
        return (
          <div className="w-[8ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "metadata.totalApprovedHours" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("metadata.totalApprovedHours", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Hours
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const hours = row.original.metadata?.totalApprovedHours || 0;
        return <div className="text-right">{hours.toFixed(1)}h</div>;
      },
    },
    {
      accessorKey: "metadata.totalApprovedAmount",
      header: () => {
        return (
          <div className="w-[8ch]">
            <Button
              variant="ghost"
              onClick={() => {
                const newSortOrder =
                  currentSort?.sortBy === "metadata.totalApprovedAmount" &&
                  currentSort?.sortOrder === "asc"
                    ? "desc"
                    : "asc";
                onSortChange?.("metadata.totalApprovedAmount", newSortOrder);
              }}
              className="h-auto p-0 font-semibold"
            >
              Amount
              <ArrowUpDown className="ml-1 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const amount = row.original.metadata?.totalApprovedAmount || 0;
        return (
          <div className="text-right font-medium">{formatCurrency(amount)}</div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (projectId: string) => {
    navigate({ to: "/projects/$projectId", params: { projectId } });
  };

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="!bg-muted/40">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleRowClick(row.original.id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No projects found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Server-side pagination */}
      {pagination && (
        <div className="flex items-center justify-center md:justify-between gap-4 flex-wrap py-4">
          <div className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total} results
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={!pagination.hasPrev}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={!pagination.hasNext}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
